import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Loader, MapPin, MapPinPlus, Route as RutaIcono, Save, Trash2, TriangleAlert, Undo2, Waypoints, X } from 'lucide-react';
import { TIPOS_PARADA, type CalculoRuta, type ParadaRutaBody, type PuntoPaso, type Ruta, type TipoParada, type TroncalDetalle } from '@tms/contratos';
import { api, ErrorApiTms } from '../api';
import { useToast } from '../state/Toast';
import { fmtDuracionMin, fmtKm } from '../util/format';
import { Mapa, type Encuadre, type LineaMapa, type PuntoMapa } from './Mapa';
import { TIPO_PARADA } from './estados';

/** Radio de geofence por defecto de una parada nueva (CLAUDE.md, «parametros»). */
const RADIO_DEFECTO_M = 300;
const ESPERA_CALCULO_MS = 600;

interface ParadaBorrador {
  /** Clave local estable para la lista (no es el id de la API). */
  clave: number;
  /** Id de la parada existente: conserva sus reglas de alerta al reordenar. */
  id: number | null;
  nombre: string;
  tipo: TipoParada;
  latitud: number;
  longitud: number;
  radioM: number;
  obligatoria: boolean;
  duracionMinEsperadaMin: number | null;
  duracionMaxEsperadaMin: number | null;
}

type Modo = 'puntos' | 'paradas';

interface Props {
  troncal: TroncalDetalle;
  /** Ruta a editar; null = ruta nueva. */
  ruta: Ruta | null;
  onCerrar: () => void;
  /** Se llamó a la API y la ruta quedó guardada. */
  onGuardada: () => void;
}

const numONull = (v: string): number | null => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Number(v));

/**
 * Editor de una ruta: el despacho marca puntos de paso en el mapa, el motor calcula la traza
 * (POST /rutas/calcular, con previsualización) y se agregan las paradas con sus reglas.
 */
export function EditorRuta({ troncal, ruta, onCerrar, onGuardada }: Props) {
  const { avisar } = useToast();
  const clave = useRef(1);
  const nuevaClave = () => clave.current++;

  const [nombre, setNombre] = useState(ruta?.nombre ?? '');
  const [porDefecto, setPorDefecto] = useState(ruta?.porDefecto ?? false);
  const [activa, setActiva] = useState(ruta?.activa ?? true);
  const [puntos, setPuntos] = useState<PuntoPaso[]>(ruta?.puntosPaso ?? []);
  const [paradas, setParadas] = useState<ParadaBorrador[]>(() =>
    (ruta?.paradas ?? []).map((p) => ({
      clave: nuevaClave(),
      id: p.id,
      nombre: p.nombre,
      tipo: p.tipo,
      latitud: p.ubicacion.latitud,
      longitud: p.ubicacion.longitud,
      radioM: p.radioM,
      obligatoria: p.obligatoria,
      duracionMinEsperadaMin: p.duracionMinEsperadaMin,
      duracionMaxEsperadaMin: p.duracionMaxEsperadaMin,
    })),
  );
  const [modo, setModo] = useState<Modo>(ruta ? 'paradas' : 'puntos');
  const [sel, setSel] = useState<number | null>(null);
  const [calculo, setCalculo] = useState<CalculoRuta | null>(ruta ? { traza: ruta.traza, km: ruta.km, duracionMin: ruta.duracionMin } : null);
  const [calculando, setCalculando] = useState(false);
  const [errorCalculo, setErrorCalculo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avisosApi, setAvisosApi] = useState<string[]>([]);
  // Id de la ruta ya guardada en esta sesión del editor: si la API devolvió avisos el editor sigue abierto
  // y un segundo «Guardar» tiene que actualizar esa ruta, no crear otra.
  const [idGuardada, setIdGuardada] = useState<number | null>(ruta?.id ?? null);

  // Los puntos con los que se calculó la traza que se ve: evita recalcular al abrir una ruta existente.
  const calculados = useRef(JSON.stringify(ruta?.puntosPaso.map((p) => [p.latitud, p.longitud]) ?? []));

  // Previsualización: al cambiar los puntos se recalcula la traza (con espera, y descartando respuestas viejas).
  useEffect(() => {
    const firma = JSON.stringify(puntos.map((p) => [p.latitud, p.longitud]));
    if (firma === calculados.current) return;
    if (puntos.length < 2) {
      calculados.current = firma;
      setCalculo(null);
      setErrorCalculo(null);
      return;
    }
    let vigente = true;
    const t = setTimeout(() => {
      setCalculando(true);
      setErrorCalculo(null);
      api
        .calcularRuta({ puntos: puntos.map((p) => ({ latitud: p.latitud, longitud: p.longitud })) })
        .then((c) => {
          if (!vigente) return;
          calculados.current = firma;
          setCalculo(c);
        })
        .catch((e: unknown) => {
          if (!vigente) return;
          setErrorCalculo(e instanceof Error ? e.message : 'No se pudo calcular la traza.');
        })
        .finally(() => {
          if (vigente) setCalculando(false);
        });
    }, ESPERA_CALCULO_MS);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [puntos]);

  const seleccionada = sel !== null ? paradas.find((p) => p.clave === sel) ?? null : null;
  const actualizarParada = (k: number, cambios: Partial<ParadaBorrador>) => setParadas((ps) => ps.map((p) => (p.clave === k ? { ...p, ...cambios } : p)));

  const alClickMapa = (lat: number, lon: number) => {
    if (modo === 'puntos') {
      setPuntos((ps) => [...ps, { latitud: lat, longitud: lon }]);
      return;
    }
    const k = nuevaClave();
    setParadas((ps) => [
      ...ps,
      {
        clave: k,
        id: null,
        nombre: `Parada ${ps.length + 1}`,
        tipo: 'OTRO',
        latitud: lat,
        longitud: lon,
        radioM: RADIO_DEFECTO_M,
        obligatoria: true,
        duracionMinEsperadaMin: null,
        duracionMaxEsperadaMin: null,
      },
    ]);
    setSel(k);
  };

  const alArrastrar = (id: string, lat: number, lon: number) => {
    if (id.startsWith('p-')) {
      const i = Number(id.slice(2));
      setPuntos((ps) => ps.map((p, j) => (j === i ? { ...p, latitud: lat, longitud: lon } : p)));
    } else if (id.startsWith('a-')) {
      actualizarParada(Number(id.slice(2)), { latitud: lat, longitud: lon });
    }
  };

  const alClickPunto = (id: string) => {
    if (id.startsWith('a-')) setSel(Number(id.slice(2)));
  };

  const mover = (i: number, d: -1 | 1) =>
    setParadas((ps) => {
      const j = i + d;
      if (j < 0 || j >= ps.length) return ps;
      const c = [...ps];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });

  // ---------- Mapa ----------
  const lineas = useMemo<LineaMapa[]>(() => {
    const out: LineaMapa[] = [];
    // Las otras rutas de la troncal, de fondo, para orientar el trazado.
    for (const r of troncal.rutas) if (r.id !== ruta?.id && r.activa) out.push({ id: `otra-${r.id}`, variante: 'ruta', coords: r.traza.coordinates });
    if (calculo && calculo.traza.coordinates.length >= 2) out.push({ id: 'previa', variante: 'previa', coords: calculo.traza.coordinates });
    return out;
  }, [troncal.rutas, ruta?.id, calculo]);

  const puntosMapa = useMemo<PuntoMapa[]>(() => {
    const ps: PuntoMapa[] = puntos.map((p, i) => ({
      id: `p-${i}`,
      lat: p.latitud,
      lon: p.longitud,
      variante: 'paso',
      texto: String(i + 1),
      titulo: p.nombre ? `Punto de paso ${i + 1}: ${p.nombre}` : `Punto de paso ${i + 1}`,
      detalle: 'Arrastralo para moverlo.',
      arrastrable: true,
      sinPopup: true,
    }));
    paradas.forEach((p, i) =>
      ps.push({
        id: `a-${p.clave}`,
        lat: p.latitud,
        lon: p.longitud,
        variante: `parada-${p.tipo}`,
        texto: String(i + 1),
        titulo: `${i + 1}. ${p.nombre}`,
        detalle: TIPO_PARADA[p.tipo].etiqueta,
        arrastrable: true,
        sinPopup: true,
        seleccionado: p.clave === sel,
      }),
    );
    return ps;
  }, [puntos, paradas, sel]);

  const encuadre = useMemo<Encuadre | undefined>(() => {
    const coords = ruta?.traza.coordinates ?? troncal.rutas.find((r) => r.activa)?.traza.coordinates ?? [];
    return coords.length ? { clave: `ed-${ruta?.id ?? 'nueva'}`, puntos: coords, zoomMax: 12 } : undefined;
  }, [ruta, troncal.rutas]);

  // ---------- Guardar ----------
  const guardar = async () => {
    setError(null);
    if (!nombre.trim()) return setError('Ponele un nombre a la ruta.');
    if (puntos.length < 2) return setError('Marcá al menos dos puntos de paso (origen y destino) para calcular la traza.');
    const sinNombre = paradas.findIndex((p) => !p.nombre.trim());
    if (sinNombre >= 0) return setError(`La parada ${sinNombre + 1} no tiene nombre.`);
    const conRango = paradas.findIndex((p) => p.duracionMinEsperadaMin !== null && p.duracionMaxEsperadaMin !== null && p.duracionMinEsperadaMin > p.duracionMaxEsperadaMin);
    if (conRango >= 0) return setError(`En la parada ${conRango + 1} la permanencia mínima supera a la máxima.`);
    const body = {
      nombre: nombre.trim(),
      puntosPaso: puntos.map((p) => ({ latitud: p.latitud, longitud: p.longitud, nombre: p.nombre?.trim() ? p.nombre.trim() : null })),
      paradas: paradas.map(
        (p): ParadaRutaBody => ({
          id: p.id,
          nombre: p.nombre.trim(),
          tipo: p.tipo,
          ubicacion: { latitud: p.latitud, longitud: p.longitud },
          radioM: p.radioM,
          obligatoria: p.obligatoria,
          duracionMinEsperadaMin: p.duracionMinEsperadaMin,
          duracionMaxEsperadaMin: p.duracionMaxEsperadaMin,
        }),
      ),
      porDefecto,
      activa,
    };
    setGuardando(true);
    try {
      const g = idGuardada !== null ? await api.actualizarRuta(idGuardada, body) : await api.crearRuta(troncal.id, body);
      setIdGuardada(g.id);
      // Las paradas nuevas ya tienen id: se conserva para que un guardado posterior no las recree.
      setParadas((ps) => ps.map((p, i) => ({ ...p, id: g.paradas[i]?.id ?? p.id })));
      const extra = g.viajesAsignados > 0 ? ` Se asignó a ${g.viajesAsignados} viaje${g.viajesAsignados === 1 ? '' : 's'} planificado${g.viajesAsignados === 1 ? '' : 's'} que no tenían ruta.` : '';
      avisar(`Ruta guardada.${extra}`);
      if (g.avisos.length > 0) {
        // Los avisos no bloquean: se muestran y el despacho decide si corrige.
        setAvisosApi(g.avisos);
        onGuardada();
        return;
      }
      onGuardada();
      onCerrar();
    } catch (e) {
      setError(e instanceof ErrorApiTms || e instanceof Error ? e.message : 'No se pudo guardar la ruta.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="troncal-grid">
      <section className="card" aria-label="Editor de ruta">
        <header>
          <h2>
            <Waypoints aria-hidden />
            {idGuardada !== null ? 'Editar ruta' : 'Nueva ruta'}
          </h2>
          <button type="button" className="btn-sm" onClick={onCerrar}>
            <X aria-hidden size={14} />
            Cerrar
          </button>
        </header>
        <div className="body editor-form">
          <label className="field">
            <span>Nombre de la ruta</span>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej.: Por RN 3, con parada en Puerto Madryn" maxLength={120} />
          </label>
          <div className="editor-acciones">
            <label className="interruptor">
              <input type="checkbox" checked={porDefecto} onChange={(e) => setPorDefecto(e.target.checked)} />
              Ruta por defecto de la troncal
            </label>
            <label className="interruptor">
              <input type="checkbox" checked={activa} onChange={(e) => setActiva(e.target.checked)} />
              Activa
            </label>
          </div>

          <div>
            <div className="sub-h">1. Puntos de paso</div>
            <p className="ayuda">Hacé clic en el mapa para marcar el origen, los puntos intermedios y el destino, en orden. El motor de ruteo calcula la traza que pasa por todos.</p>
            <div className="editor-lista" style={{ marginTop: 8 }}>
              {puntos.map((p, i) => (
                <div key={i} className="editor-fila">
                  <span className="num" style={{ borderRadius: 4 }}>{i + 1}</span>
                  <span className="txt">
                    {i === 0 ? 'Origen' : i === puntos.length - 1 ? 'Destino' : 'Intermedio'}
                    <small>
                      {p.latitud.toFixed(4)}, {p.longitud.toFixed(4)}
                    </small>
                    <input
                      className="punto-nombre"
                      value={p.nombre ?? ''}
                      maxLength={120}
                      placeholder="Nombre del punto (opcional)"
                      aria-label={`Nombre del punto de paso ${i + 1}`}
                      onChange={(e) => setPuntos((ps) => ps.map((x, j) => (j === i ? { ...x, nombre: e.target.value === '' ? null : e.target.value } : x)))}
                    />
                  </span>
                  <button type="button" title="Quitar punto" aria-label={`Quitar el punto de paso ${i + 1}`} onClick={() => setPuntos((ps) => ps.filter((_, j) => j !== i))}>
                    <Trash2 aria-hidden />
                  </button>
                </div>
              ))}
              {puntos.length === 0 && <p className="muted" style={{ fontSize: 12 }}>Todavía no marcaste ningún punto.</p>}
            </div>
            <div className="editor-acciones" style={{ marginTop: 8 }}>
              <button type="button" className="btn-sm" disabled={puntos.length === 0} onClick={() => setPuntos((ps) => ps.slice(0, -1))}>
                <Undo2 aria-hidden size={14} />
                Deshacer el último
              </button>
              <button type="button" className={`btn-sm ${modo === 'puntos' ? 'primary' : ''}`} onClick={() => setModo('puntos')} aria-pressed={modo === 'puntos'}>
                <MapPin aria-hidden size={14} />
                Marcar puntos
              </button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12 }} role="status">
              {calculando && (
                <span className="muted" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Loader className="spin" aria-hidden size={13} />
                  Calculando la traza…
                </span>
              )}
              {!calculando && calculo && (
                <span className="green" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <RutaIcono aria-hidden size={13} />
                  {fmtKm(calculo.km)} · {fmtDuracionMin(calculo.duracionMin)} estimadas
                </span>
              )}
              {!calculando && !calculo && !errorCalculo && puntos.length < 2 && <span className="muted">Con dos puntos ya se calcula la traza.</span>}
              {errorCalculo && (
                <span className="red" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <TriangleAlert aria-hidden size={13} />
                  {errorCalculo}
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="sub-h">2. Paradas y sus reglas</div>
            <p className="ayuda">
              Elegí «Agregar paradas» y hacé clic en el mapa. Una parada obligatoria que no se cumple genera «Parada omitida»; si la permanencia supera el máximo, «Parada excedida».
            </p>
            <div className="editor-acciones" style={{ margin: '8px 0' }}>
              <button type="button" className={`btn-sm ${modo === 'paradas' ? 'primary' : ''}`} onClick={() => setModo('paradas')} aria-pressed={modo === 'paradas'}>
                <MapPinPlus aria-hidden size={14} />
                Agregar paradas
              </button>
            </div>
            <div className="editor-lista">
              {paradas.map((p, i) => {
                const Icono = TIPO_PARADA[p.tipo].icono;
                return (
                  <div key={p.clave} className={`editor-fila ${p.clave === sel ? 'sel' : ''}`}>
                    <span className="num">{i + 1}</span>
                    <button type="button" className="txt" style={{ border: 0, background: 'none', padding: 0, justifyContent: 'flex-start', textAlign: 'left', display: 'block' }} onClick={() => setSel(p.clave)}>
                      {p.nombre || 'Sin nombre'}
                      <small>
                        <Icono aria-hidden size={11} style={{ verticalAlign: '-1px' }} /> {TIPO_PARADA[p.tipo].etiqueta} · radio {p.radioM} m · {p.obligatoria ? 'obligatoria' : 'opcional'}
                      </small>
                    </button>
                    <button type="button" title="Subir" aria-label={`Subir la parada ${i + 1}`} disabled={i === 0} onClick={() => mover(i, -1)}>
                      <ArrowUp aria-hidden />
                    </button>
                    <button type="button" title="Bajar" aria-label={`Bajar la parada ${i + 1}`} disabled={i === paradas.length - 1} onClick={() => mover(i, 1)}>
                      <ArrowDown aria-hidden />
                    </button>
                    <button
                      type="button"
                      title="Quitar parada"
                      aria-label={`Quitar la parada ${i + 1}`}
                      onClick={() => {
                        setParadas((ps) => ps.filter((x) => x.clave !== p.clave));
                        if (sel === p.clave) setSel(null);
                      }}
                    >
                      <Trash2 aria-hidden />
                    </button>
                  </div>
                );
              })}
              {paradas.length === 0 && <p className="muted" style={{ fontSize: 12 }}>La ruta no tiene paradas. Es válido: solo se vigila el desvío.</p>}
            </div>

            {seleccionada && (
              <div className="parada-form" style={{ marginTop: 10 }}>
                <strong style={{ fontSize: 12 }}>Parada {paradas.findIndex((p) => p.clave === seleccionada.clave) + 1}</strong>
                <div className="fila2">
                  <label className="field">
                    <span>Nombre</span>
                    <input value={seleccionada.nombre} onChange={(e) => actualizarParada(seleccionada.clave, { nombre: e.target.value })} maxLength={120} />
                  </label>
                  <label className="field">
                    <span>Tipo</span>
                    <select value={seleccionada.tipo} onChange={(e) => actualizarParada(seleccionada.clave, { tipo: e.target.value as TipoParada })}>
                      {TIPOS_PARADA.map((t) => (
                        <option key={t} value={t}>
                          {TIPO_PARADA[t].etiqueta}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="fila2">
                  <label className="field">
                    <span>Radio del geofence (m)</span>
                    <input type="number" min={50} max={5000} step={10} value={seleccionada.radioM} onChange={(e) => actualizarParada(seleccionada.clave, { radioM: Math.max(50, Number(e.target.value) || RADIO_DEFECTO_M) })} />
                  </label>
                  <label className="interruptor" style={{ alignSelf: 'end', height: 36 }}>
                    <input type="checkbox" checked={seleccionada.obligatoria} onChange={(e) => actualizarParada(seleccionada.clave, { obligatoria: e.target.checked })} />
                    Obligatoria
                  </label>
                </div>
                <div className="fila2">
                  <label className="field">
                    <span>Permanencia mínima esperada (min)</span>
                    <input type="number" min={0} value={seleccionada.duracionMinEsperadaMin ?? ''} onChange={(e) => actualizarParada(seleccionada.clave, { duracionMinEsperadaMin: numONull(e.target.value) })} placeholder="Sin dato" />
                  </label>
                  <label className="field">
                    <span>Permanencia máxima (min)</span>
                    <input type="number" min={0} value={seleccionada.duracionMaxEsperadaMin ?? ''} onChange={(e) => actualizarParada(seleccionada.clave, { duracionMaxEsperadaMin: numONull(e.target.value) })} placeholder="Sin límite" />
                  </label>
                </div>
                <p className="ayuda">Los umbrales de nivel de las alertas de esta parada se configuran en Configuración de alertas, con ámbito «Parada de ruta».</p>
              </div>
            )}
          </div>

          {avisosApi.length > 0 && (
            <div className="banner aviso" role="status" style={{ alignItems: 'flex-start' }}>
              <TriangleAlert aria-hidden />
              <div>
                <strong>La ruta se guardó con avisos:</strong>
                <ul className="aviso-lista">
                  {avisosApi.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {error && (
            <div className="banner error" role="alert">
              <TriangleAlert aria-hidden />
              {error}
            </div>
          )}
          <div className="editor-acciones">
            <button type="button" className="primary" disabled={guardando || calculando} onClick={() => void guardar()}>
              <Save aria-hidden size={14} />
              {guardando ? 'Guardando…' : 'Guardar ruta'}
            </button>
            <button type="button" onClick={onCerrar}>
              {avisosApi.length > 0 ? 'Listo' : 'Cancelar'}
            </button>
          </div>
        </div>
      </section>

      <section className="card" aria-label="Mapa del editor" style={{ overflow: 'hidden' }}>
        <div className="editor-mapa">
          <span className="modo-mapa" role="status">
            {modo === 'puntos' ? <MapPin aria-hidden size={13} /> : <MapPinPlus aria-hidden size={13} />}
            {modo === 'puntos' ? 'Un clic agrega un punto de paso' : 'Un clic agrega una parada'}
          </span>
          <Mapa lineas={lineas} puntos={puntosMapa} encuadre={encuadre} onClickMapa={alClickMapa} onArrastrarPunto={alArrastrar} onClickPunto={alClickPunto} />
        </div>
        <div className="foot">Los círculos numerados con cuadrado son puntos de paso; los redondos, paradas. Se pueden arrastrar para ajustarlos.</div>
      </section>
    </div>
  );
}
